import _ from 'lodash'
import i18n from 'scoreboard/components/i18n'
import globals from 'scoreboard/components/globals'
import calendars from 'scoreboard/components/calendars'
import listStreamer from 'scoreboard/reports/common/list-streamer'
import constants from 'scoreboard/components/constants'
import assignLinkGroups from 'scoreboard/components/assign-link-groups'
import BaseReportModel from 'scoreboard/reports/common/base-report-model'
import permissions from 'scoreboard/components/permissions'
import projects from 'scoreboard/projects/projects'
import {isSpectating} from 'scoreboard/components/spectator'

const StandardReportBaseModel = BaseReportModel.extend({

	periodSelector: true,

	defaults: function() {
		return {
			projectId: projects.id,
			organizationId: globals.orgId,
			relativePeriodId: globals.periodId,
			currentCalendarId: calendars.periodsById[globals.periodId].calendar.id,
			type: 'report',
			label: i18n.get('report.customReport.title'),
			columns: {
				columns: ['label']
			},
			showAbbreviatedValues: true
		}
	},

	_listenForSortChanges () {
		this.on('change:sortField change:sortPeriod change:ascending', () => {
			// #18055 if user doesn't have permission to modify report, don't mark as dirty
			// #28577 do not dirty model when spectating
			if (permissions.reports(this.get('organizationId')) && !isSpectating()) {
				this.set({ 'dirty': true }, { silent: true })
			}
			this.stale = true
		})
		this.on('change:sortField', function(model, val, opts) {
			var changeOpts = opts || {}
			if (changeOpts.unset) {
				this.unset('sortType', {silent:true})
				this.unset('sortPeriodIndex', {silent:true})
			}
		})
	},

	initialize: function(attrs, opts) {
		BaseReportModel.prototype.initialize.apply(this, arguments)

		//A report is dirty if any of it's attributes have changed from their
		//persisted state. A report is stale if it's results need to be fetched.
		this.stale = true
		this.on('change:relativePeriodId', function() {
			this.stale = true
		}.bind(this))
		this.on('change:filters change:columns change:label change:showAbbreviatedValues', () => {
			this.set({ 'dirty': true }, { silent: true })
			this.stale = true
		})
		this._listenForSortChanges()
		this.metadata = {}
	},

	validate: function(attributes, options) {
		var errors = {}
		if (_.isEmpty(attributes.label)) {
			errors.label = i18n.get('common.error.required', i18n.get('report.title'))
		}

		return this.formatErrors(errors, options)
	},

	fetch: function(options = {}) {
		if (!this.omitProjectFilter) {
			//ensure projects filter is set
			let projectsFilter = _.get(this.attributes, 'filters.projectIds[0].values[0]', null)
			if (projectsFilter == null) {
				_.set(this.attributes, 'filters.projectIds', [{values: [projects.id]}])
			}
		} else {
			_.unset(this.attributes, 'filters.projectIds')
		}

		assignLinkGroups(_.flatMap(this.get('filters')))
		var opts = options || {}
		let urlParams =  _.assign(_.omit(opts, 'success', 'error', 'parseForm'), {showCount: true})
		var params = {
			method: 'GET',
			url: this.urlRoot(this.get('id'), _.assign(
				urlParams, this.periodParams({results: true}))),
			contentType: constants.CONTENT_TYPES.APPLICATION_JSON,
			stream: 'results',
			success: function(data, stat, xhr) {
				this.stale = false
				if (opts.success) {
					opts.success.apply(opts.context, arguments)
				}
			}.bind(this),
			parseForm: (key, val) => {
				if (key == 'form') {
					this._setMetadata(val.metadata)
				}
				if (opts.parseForm) {
					opts.parseForm(key, val)
				}
			}
		}
		if (opts.error) {
			params.error = opts.error.bind(this)
		}

		if (this.isNew() || this.get('dirty') || options.force) {
			let periodData = this.periodParams()
			this.set(periodData, {silent: true})
			if (_.get(opts, 'embeddedToken')) {
				urlParams.embeddedToken = opts.embeddedToken
			}
			params = _.assign(params, periodData, {
				url: this.urlRoot('results', urlParams),
				method: 'POST',
				data: JSON.stringify(this.toJSON(options))
			})
		}

		//clear the previous metadata, we're making a new request
		this.metadata = {}

		// chunk size can be important to pass in when we aren't grabbing
		// multiple chunks of data and are relying on the initial query
		if (options?.chunkSize) {
			params = _.extend({}, params, { chunkSize: options.chunkSize })
		}
		this._latestFetch = listStreamer(this, params)
		return this._latestFetch
	},

	/**
	 * Returns a promise which is resolved when the metadata for the results
	 * has been determined
	 */
	resultsMetadata() {
		//return a promise of metadata if it's been received from the last fetch, otherwise
		//an empty object if the fetch errored or the hasn't been a fetch
		return new Promise(res => {
			if (this._streamPromise) {
				return this._streamPromise.always(() => res(this.metadata))
			} else {
				res(this.metadata)
			}
		})
	},

	//adds or edits an existing filter with the given value.  The key value is the name of the filter (e.g. 'label',
	//'weight').  If an index is given it will replace the existing value at that index if it exists, otherwise the
	//data is appended to the end of the filter list for that given filter
	setFilter: function(value, key, index) {
		if (_.isNil(value)) { return }

		//add the filter and trigger backbone change.  Clone so cancel can go back to saved state #17953
		let filters = _.cloneDeep(this.get('filters'))
		let filterValue = filters[key] || []

		if (_.isFinite(index) && !_.isUndefined(filterValue[index])) {
			filterValue[index] = value
		} else {
			filterValue.push(value)
		}

		filters[key] = filterValue

		//unset/set to trigger model changes
		this.unset('filters', {silent: true})
		this.set('filters', filters)
	}

})

export default StandardReportBaseModel

StandardReportBaseModel.__filename__ = 'reports/common/standard-report-base-model.js'
